// Canonical resolution of an itinerary item's EFFECTIVE travellers — the
// single source of truth every consumer (Operations list, reports, WhatsApp
// templates, planner rows, filters) reads the pax count through.
//
// `itinerary_item.travellers` is a denormalised projection, not an authored
// field: the write paths copy it down from the item's chapter / itinerary
// version (`itinerary-items.create`) or from its parent item
// (`itinerary-items.createSubitem`), and the traveller cascades keep it in
// sync. Two consequences follow, and they are the reason a shared resolver
// exists instead of ad-hoc `travellers.people.length` reads:
//
//   1. An EMPTY composition means "this row has no pax of its own", never
//      "zero travellers". The column legitimately holds `null`, `{}`,
//      `{ people: [] }` or `[]` — rows written before subitem inheritance
//      landed, and rows whose form cleared the composition.
//   2. Resolution therefore falls through the projection chain — the item's
//      own composition, then its PARENT item's (a subitem is a sub-service of
//      its parent and shares its pax), then the itinerary version's default —
//      and the first NON-EMPTY source wins. An explicit override is always
//      the item's own value, so it still wins.
//
// The column also carries several historical shapes; `travellerEntries`
// normalises all of them once so no consumer has to.
//
// `effectiveTravellersSql` / `travellerCountSql` in `@travel-planner/db/sql`
// are the SQL mirror of these exact rules, applied at the query boundary so
// server-rendered surfaces (reports, WhatsApp, filters) resolve identically to
// the client. `effective-travellers.integration.test.ts` runs the same
// fixture matrix through both to keep them from drifting.

type TravellerEntryRecord = Record<string, unknown>;

const asEntryArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

/**
 * The traveller entries carried by a `travellers` jsonb, in any of its stored
 * shapes:
 *   - `{ people: [{ travelerTypeId, title }] }` — the canonical envelope
 *     (one entry per person);
 *   - `[{ id | travellerId, title, count }]` — the pricing engine's
 *     aggregated array;
 *   - `[{ quantity, travelerType: { title } }]` — the legacy version shape.
 * Anything else (null, a number, `{}`, a non-object entry) contributes
 * nothing.
 */
export const travellerEntries = (raw: unknown): TravellerEntryRecord[] => {
  if (raw == null || typeof raw !== 'object') return [];
  const entries = Array.isArray(raw)
    ? raw
    : asEntryArray((raw as { people?: unknown }).people);
  return entries.filter(
    (entry): entry is TravellerEntryRecord =>
      entry != null && typeof entry === 'object' && !Array.isArray(entry)
  );
};

// Head-count of one entry: the aggregated shapes carry it as `count` /
// `quantity`; a canonical `{ people }` entry is one person.
const entryHeadCount = (entry: TravellerEntryRecord): number => {
  const raw = typeof entry.count === 'number' ? entry.count : entry.quantity;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 1;
  return raw > 0 ? Math.floor(raw) : 0;
};

/** Head-count carried by a `travellers` jsonb (0 when it carries none). */
export const travellerCount = (raw: unknown): number =>
  travellerEntries(raw).reduce(
    (total, entry) => total + entryHeadCount(entry),
    0
  );

/** One traveller body. `title` is the traveller-type label when stored. */
export type TravellerHead = {
  travellerTypeId: string | null;
  title: string | null;
};

const asId = (entry: TravellerEntryRecord): string | null => {
  const raw = entry.travelerTypeId ?? entry.travellerTypeId ?? entry.id;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
};

const asTitle = (entry: TravellerEntryRecord): string | null => {
  const nested = entry.travelerType;
  const raw =
    typeof entry.title === 'string'
      ? entry.title
      : nested != null && typeof nested === 'object'
        ? (nested as TravellerEntryRecord).title
        : null;
  return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
};

/**
 * The individual traveller bodies a `travellers` jsonb carries — ONE entry per
 * head, so the aggregated shapes (`[{ id, title, count: 3 }]`,
 * `[{ quantity, travelerType }]`) expand to 3 / `quantity` heads rather than
 * one. `travellerHeads(raw).length === travellerCount(raw)` for every shape, by
 * construction — that is what stops a surface printing "5 travellers" beside a
 * 2-name list (`TCO-OPS08-3649`). Use this wherever a count and the names it
 * labels must agree; use `travellerCount` alone when only the number is needed.
 */
export const travellerHeads = (raw: unknown): TravellerHead[] =>
  travellerEntries(raw).flatMap((entry) => {
    const head = { travellerTypeId: asId(entry), title: asTitle(entry) };
    return Array.from({ length: entryHeadCount(entry) }, () => ({ ...head }));
  });

/**
 * Whether a `travellers` jsonb carries a real composition. `false` for null,
 * an empty envelope (`{ people: [] }`), an empty array and every non-object —
 * the single definition of "unset" that resolution falls through.
 */
export const hasTravellers = (raw: unknown): boolean => travellerCount(raw) > 0;

/**
 * First non-empty travellers payload in the projection chain, else null.
 * Callers pass the sources most-specific first — e.g.
 * `resolveEffectiveTravellers(item.travellers, parent.travellers, version.travellers)`.
 * Returns the source verbatim (never a re-serialised copy) so real
 * traveller-type ids, titles and per-person fields survive.
 */
export const resolveEffectiveTravellers = (
  ...sources: readonly unknown[]
): unknown => sources.find((source) => hasTravellers(source)) ?? null;
